"use client";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import styles from "./login.module.scss";
import { Eye, EyeSlash, X, ExclamationCircle } from "react-bootstrap-icons";
import { useAuth } from "@/providers/auth-context";
import { requestToCreateResource } from "@/services/Fetcher";
import { getTermsAccepted, setTermsAccepted as persistTermsAccepted } from "@/services/Cookies.client";
import submitUserLogin from "./actions";
import submitUserRegistration from "../register/actions";
function ModalBase({
  show,
  title,
  children,
  onClose,
  footer,
}: {
  show: boolean;
  title?: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} ref={ref} tabIndex={-1}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>{title}</div>
          <button type="button" aria-label="Close" className={styles.iconBtn} onClick={onClose}>
            <X />
          </button>
        </div>
        <div className={styles.modalBody}>{children}</div>
        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState<"login" | "register">("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showInvalid, setShowInvalid] = useState(false);
  const [lastTriedEmail, setLastTriedEmail] = useState("");

  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [regShowPw, setRegShowPw] = useState(false);
  const [regShowConfirm, setRegShowConfirm] = useState(false);
  const [regAgreed, setRegAgreed] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState("");
  const [regFieldErrors, setRegFieldErrors] = useState<Record<string, string>>({});
  const [regSuccess, setRegSuccess] = useState(false);

  const [showConsent, setShowConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const consentBodyRef = useRef<HTMLDivElement | null>(null);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { login } = useAuth();
  const nextParam = searchParams.get("redirect") ?? searchParams.get("next");

  useEffect(() => {
    if (!getTermsAccepted()) {
      setShowConsent(true);
    } else {
      setTermsAccepted(true);
    }
  }, []);

  const switchMode = (next: "login" | "register") => {
    setMode(next);
    setRegError("");
    setRegFieldErrors({});
  };

  const openConsentModal = () => {
    setHasScrolledToBottom(false);
    setShowConsent(true);
  };

  const handleConsentScroll = () => {
    const el = consentBodyRef.current;
    if (!el) return;
    setHasScrolledToBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 50);
  };

  const handleAgreeInModal = () => {
    if (!hasScrolledToBottom) return;
    persistTermsAccepted();
    setTermsAccepted(true);
    setRegAgreed(true);
    setShowConsent(false);
    setHasScrolledToBottom(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsLoading(true);
    setLastTriedEmail(email);
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);
    formData.append("user_agent", navigator.userAgent);
    const result = await submitUserLogin(formData);
    if (!result.ok) {
      result.reason === "EMAIL_NOT_VERIFIED" ? setShowVerify(true) : setShowInvalid(true);
      setIsLoading(false);
      return;
    }
    if (!result.tokens.has_consented) {
      requestToCreateResource("/api/consent/", {}).catch(() => {});
    }
    setIsRedirecting(true);
    login();
    router.push(nextParam ? decodeURIComponent(nextParam) : "/profile");
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError("");
    setRegFieldErrors({});

    if (regPassword !== regConfirm) {
      setRegFieldErrors({ confirm: "Passwords do not match." });
      return;
    }
    if (regPassword.length < 8) {
      setRegFieldErrors({ password: "Use at least 8 characters." });
      return;
    }
    if (!regAgreed) {
      setRegFieldErrors({ consent: "You must agree to the privacy policy." });
      return;
    }

    setRegLoading(true);
    const formData = new FormData();
    formData.append("email", regEmail);
    formData.append("password", regPassword);
    formData.append("has_consented", "true");

    const result = await submitUserRegistration(formData);
    setRegLoading(false);

    if (result.success) {
      setRegSuccess(true);
    } else {
      if (result.fieldErrors) {
        const fe: Record<string, string> = {};
        if (result.fieldErrors.email) fe.email = result.fieldErrors.email;
        if (result.fieldErrors.password) fe.password = result.fieldErrors.password;
        setRegFieldErrors(fe);
      }
      setRegError(result.message || "Registration failed.");
    }
  };

  const imagePanel = (
    <div className={styles.imagePanel}>
      <h2 className={styles.imageTitle}>Welcome to AI2Peat</h2>
      <p className={styles.imageDesc}>
        Be part of the global effort to monitor, restore, and conserve
        peatlands through data-driven insights and collaborative
        environmental stewardship.
      </p>
    </div>
  );

  if (isRedirecting) {
    return (
      <div className={styles.loggingInScreen}>
        <div className={styles.loggingInCard}>
          <img src="/logo_black.svg" alt="AI2Peat" className={styles.loggingInLogo} />
          <div className={styles.loggingInSpinner} />
          <p className={styles.loggingInText}>Logging in…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardContainer}>
          <div className={`${styles.track} ${mode === "register" ? styles.trackRegister : ""}`}>

            <div className={styles.screen}>
              {imagePanel}
              <div className={styles.formPanel}>
                <div className={styles.logoWrapper}>
                  <img src="/logo_black.svg" alt="AI2Peat" />
                </div>
                <h1>Welcome</h1>
                <p className={styles.subtitle}>
                  Join a community of environmental experts working together to preserve vital carbon sinks.
                </p>
                <form onSubmit={handleLogin} noValidate className={styles.loginForm}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email address *</label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                        required
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="password">Password *</label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.forgotPasswordLink}
                    onClick={() => setShowForgotModal(true)}
                    disabled={isLoading}
                  >
                    Reset password
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={!email || !password || isLoading}
                  >
                    {isLoading ? "Logging in…" : "Continue"}
                  </button>
                </form>
                <p className={styles.footerText}>
                  Don&apos;t have an account?{" "}
                  <button className={styles.switchLink} onClick={() => switchMode("register")}>
                    Sign up
                  </button>
                </p>
              </div>
            </div>

            <div className={styles.screen}>
              <div className={styles.formPanel}>
                <div className={styles.logoWrapper}>
                  <img src="/logo_black.svg" alt="AI2Peat" />
                </div>
                <h1>Create account</h1>
                <p className={styles.subtitle}>
                  Join the global effort to monitor and restore peatlands.
                </p>

                {regSuccess ? (
                  <div className={styles.noteSuccess}>
                    <p>Account created! Check your inbox to verify your email.</p>
                    <button
                      className={styles.submitButton}
                      style={{ marginTop: "20px" }}
                      onClick={() => { setRegSuccess(false); switchMode("login"); }}
                    >
                      Back to Login
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleRegister} noValidate className={styles.loginForm}>
                    <div className={styles.formGroup}>
                      <label htmlFor="reg-email">Email address *</label>
                      <div className={styles.inputWrapper}>
                        <input
                          id="reg-email"
                          type="email"
                          placeholder="Email address"
                          value={regEmail}
                          onChange={(e) => setRegEmail(e.target.value.trim().toLowerCase())}
                          required
                          autoComplete="email"
                          disabled={regLoading}
                        />
                      </div>
                      {regFieldErrors.email && <p className={styles.errorText}>{regFieldErrors.email}</p>}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="reg-password">Password *</label>
                      <div className={styles.inputWrapper}>
                        <input
                          id="reg-password"
                          type={regShowPw ? "text" : "password"}
                          placeholder="Password (min. 8 characters)"
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          required
                          disabled={regLoading}
                        />
                        <button
                          type="button"
                          className={styles.passwordToggle}
                          onClick={() => setRegShowPw(!regShowPw)}
                          aria-label={regShowPw ? "Hide" : "Show"}
                        >
                          {regShowPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {regFieldErrors.password && <p className={styles.errorText}>{regFieldErrors.password}</p>}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="reg-confirm">Confirm password *</label>
                      <div className={styles.inputWrapper}>
                        <input
                          id="reg-confirm"
                          type={regShowConfirm ? "text" : "password"}
                          placeholder="Confirm password"
                          value={regConfirm}
                          onChange={(e) => setRegConfirm(e.target.value)}
                          required
                          disabled={regLoading}
                        />
                        <button
                          type="button"
                          className={styles.passwordToggle}
                          onClick={() => setRegShowConfirm(!regShowConfirm)}
                          aria-label={regShowConfirm ? "Hide" : "Show"}
                        >
                          {regShowConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {regFieldErrors.confirm && <p className={styles.errorText}>{regFieldErrors.confirm}</p>}
                    </div>

                    <div className={styles.consentRow}>
                      <div
                        className={`${styles.consentIndicator} ${regAgreed ? styles.consentIndicatorAgreed : ""}`}
                        onClick={openConsentModal}
                        role="checkbox"
                        aria-checked={regAgreed}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openConsentModal(); }}}
                      >
                        {regAgreed ? "✓" : ""}
                      </div>
                      <span className={styles.consentLabel} onClick={openConsentModal}>
                        I agree to the{" "}
                        <button type="button" className={styles.consentLink} onClick={(e) => { e.stopPropagation(); openConsentModal(); }}>
                          Privacy Policy
                        </button>
                      </span>
                    </div>
                    {regFieldErrors.consent && <p className={styles.errorText}>{regFieldErrors.consent}</p>}
                    {regError && <p className={styles.errorText}>{regError}</p>}

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={!regEmail || !regPassword || !regConfirm || !regAgreed || regLoading}
                    >
                      {regLoading ? "Creating account…" : "Create account"}
                    </button>
                  </form>
                )}

                <p className={styles.footerText}>
                  Already have an account?{" "}
                  <button className={styles.switchLink} onClick={() => switchMode("login")}>
                    Log in
                  </button>
                </p>
              </div>
              {imagePanel}
            </div>

          </div>
        </div>
      </div>

      <ForgotPasswordModal
        show={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        initialEmail={email}
      />
      <VerifyEmailModal
        show={showVerify}
        email={lastTriedEmail}
        onClose={() => { setEmail(""); setPassword(""); setShowVerify(false); }}
        onChangeEmail={() => { setEmail(""); setPassword(""); setShowVerify(false); }}
        onResent={() => { setEmail(""); setPassword(""); }}
      />
      <InvalidCredentialsModal
        show={showInvalid}
        onClose={() => setShowInvalid(false)}
      />
      <ConsentModal
        show={showConsent}
        hasScrolledToBottom={hasScrolledToBottom}
        onScroll={handleConsentScroll}
        onAgree={handleAgreeInModal}
        consentBodyRef={consentBodyRef}
        isInitialFlow={!termsAccepted}
        onClose={() => {
          if (termsAccepted) {
            setShowConsent(false);
            setHasScrolledToBottom(false);
          }
        }}
      />
    </>
  );
}

function VerifyEmailModal({ show, email, onClose, onChangeEmail, onResent }: {
  show: boolean; email: string; onClose: () => void; onChangeEmail: () => void; onResent: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    if (!email || working) return;
    setWorking(true);
    try { await requestToCreateResource("/api/resend-verification/", { email }); setSent(true); onResent(); }
    catch { setSent(false); } finally { setWorking(false); }
  };
  return (
    <ModalBase show={show} title="Email verification required" onClose={onClose}
      footer={
        <div className={styles.modalFooterButtons}>
          <button className={styles.secondaryButton} onClick={onChangeEmail} disabled={working}>Change email</button>
          <button className={styles.primaryButton} onClick={resend} disabled={working}>
            {working ? "Sending…" : "Resend verification link"}
          </button>
        </div>
      }
    >
      <p className={styles.modalText}>Your account for <span className={styles.emailHighlight}>{email}</span> isn&apos;t verified yet.</p>
      <p className={styles.modalText}>Send a new verification link to this email?</p>
      {sent && <div className={styles.noteSuccess}>We have sent a new verification link. Please check your inbox (and spam).</div>}
    </ModalBase>
  );
}

function ForgotPasswordModal({ show, onClose, initialEmail = "" }: {
  show: boolean; onClose: () => void; initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail.trim().toLowerCase());
  const [confirmEmail, setConfirmEmail] = useState(initialEmail.trim().toLowerCase());
  const [message, setMessage] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (show) { setEmail(initialEmail.trim().toLowerCase()); setConfirmEmail(initialEmail.trim().toLowerCase()); setMessage(""); setSentTo(null); }
  }, [initialEmail, show]);

  const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  const emailsMatch = email && confirmEmail && email === confirmEmail;
  const emailValid = isEmail(email);
  const canSend = emailValid && emailsMatch && !working;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSend) return;
    setWorking(true);
    try { await requestToCreateResource(`/auth/password/reset/`, { email }); setSentTo(email); }
    catch { setSentTo(email); } finally { setWorking(false); }
  };

  return (
    <ModalBase show={show} title={<span className={styles.modalTitleWithIcon}>FORGOT PASSWORD?</span>} onClose={onClose}
      footer={sentTo ? (
        <button className={styles.primaryButton} onClick={onClose}>Done</button>
      ) : (
        <div className={styles.modalFooterButtons}>
          <button className={styles.secondaryButton} onClick={onClose} disabled={working}>Close</button>
          <button className={styles.primaryButton} form="forgot-form" type="submit" disabled={!canSend}>
            {working ? "Sending…" : "Send Reset Link"}
          </button>
        </div>
      )}
    >
      {sentTo ? (
        <div className={styles.noteSuccess} role="status" aria-live="polite">
          If an account exists for <strong>{sentTo}</strong>, we&apos;ve sent a reset link.
          <div className={styles.modalNote}>Please check your inbox (and spam) and follow the instructions.</div>
        </div>
      ) : (
        <form id="forgot-form" onSubmit={onSubmit} noValidate>
          <p className={styles.modalText}>Enter your email address and we&apos;ll send you a reset link.</p>
          <div className={styles.formGroup}>
            <label htmlFor="fp-email">Email Address</label>
            <input id="fp-email" name="email" type="email" placeholder="Enter your email" required autoFocus autoComplete="email"
              disabled={working} value={email}
              onChange={(e) => { setEmail(e.target.value.trim().toLowerCase()); setMessage(""); }}
              aria-invalid={email.length > 0 && !emailValid} />
            {email.length > 0 && !emailValid && <div className={styles.errorText} role="alert">Enter a valid email address.</div>}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="fp-confirm">Confirm Email Address</label>
            <input id="fp-confirm" name="confirm_email" type="email" placeholder="Enter email again" required autoComplete="email"
              disabled={working} value={confirmEmail}
              onChange={(e) => { setConfirmEmail(e.target.value.trim().toLowerCase()); setMessage(""); }}
              aria-invalid={confirmEmail.length > 0 && !emailsMatch} />
            {confirmEmail.length > 0 && !emailsMatch && <div className={styles.errorText} role="alert">Emails do not match.</div>}
          </div>
          {message && <p className={styles.errorText}>{message}</p>}
          <div className={styles.modalNote} role="alert" aria-live="polite">
            We&apos;ll email you a reset link. For security, we won&apos;t confirm whether an account exists.
          </div>
        </form>
      )}
    </ModalBase>
  );
}

function InvalidCredentialsModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  return (
    <ModalBase show={show}
      title={<span className={styles.modalTitleWithIcon}><ExclamationCircle size={20} className={styles.errorIcon} /> Invalid credentials</span>}
      onClose={onClose}
      footer={<button className={styles.primaryButton} onClick={onClose} autoFocus>OK</button>}
    >
      <div className={styles.modalAlertContent}>
        <p className={styles.modalText}>The email or password you entered is incorrect.</p>
        <p className={styles.modalText}>Please double-check your login details and try again.</p>
      </div>
    </ModalBase>
  );
}

function ConsentModal({ show, hasScrolledToBottom, onScroll, onAgree, onClose, consentBodyRef, isInitialFlow }: {
  show: boolean;
  hasScrolledToBottom: boolean;
  onScroll: () => void;
  onAgree: () => void;
  onClose: () => void;
  consentBodyRef: React.RefObject<HTMLDivElement | null>;
  isInitialFlow?: boolean;
}) {
  return (
    <ModalBase
      show={show}
      title={<span className={styles.modalTitleWithIcon}>📄 User Consent Agreement</span>}
      onClose={isInitialFlow ? () => {} : onClose}
      footer={
        <div className={styles.modalFooterButtons}>
          {!isInitialFlow && (
            <button className={styles.secondaryButton} onClick={onClose}>Cancel</button>
          )}
          <button
            className={styles.primaryButton}
            onClick={onAgree}
            disabled={!hasScrolledToBottom}
            title={!hasScrolledToBottom ? "Please scroll to the bottom first" : ""}
          >
            I Agree to These Terms
          </button>
        </div>
      }
    >
      <div className={styles.consentBody} ref={consentBodyRef} onScroll={onScroll}>
        <div className={styles.consentIntro}>
          <h5>Welcome to AI2Peat</h5>
          <p>A collaborative platform built by the AI2Peat project to support the monitoring, restoration, and sustainable management of Ireland&apos;s peatlands through data, research, and community collaboration.</p>
        </div>
        <div className={styles.consentSection}>
          <h6>1. Data Collection &amp; Use</h6>
          <p>We collect your personal information (e.g., name, email, affiliation, data) to:</p>
          <ul>
            <li>Create and manage your user profile</li>
            <li>Enable your participation in community features (Directory, Projects, Events, Wishes)</li>
            <li>Improve and personalize your experience across the platform</li>
            <li>Securely process and store user-submitted data to enable future access, analysis, and platform improvement</li>
          </ul>
        </div>
        <div className={styles.consentSection}>
          <h6>2. Public Visibility</h6>
          <p>Your name, organisation, and related activities may appear publicly in directories, event listings, project affiliations, or publications to foster collaboration and awareness. You can opt out by updating your profile or contacting us directly.</p>
        </div>
        <div className={styles.consentSection}>
          <h6>3. Research, Reporting &amp; Analytics</h6>
          <p>We may use anonymized user data to support scientific research, usage reports, and platform evaluation. Identifiable information will never be shared without your explicit consent.</p>
        </div>
        <div className={styles.consentSection}>
          <h6>4. Third-Party Tools</h6>
          <p>AI2Peat uses secure, industry-standard third-party tools for GIS mapping and collaboration. All data handling complies with relevant data protection regulations.</p>
        </div>
        <div className={styles.consentSection}>
          <h6>5. Security Monitoring &amp; IP Address</h6>
          <p>To protect your account and detect unauthorised access, AI2Peat records your IP address and device information each time you log in. This data is:</p>
          <ul>
            <li>Used solely for security purposes : specifically to detect logins from new or unrecognised devices</li>
            <li>Never shared with third parties or used for advertising</li>
            <li>Stored securely and retained only as long as necessary for account security</li>
            <li>Used to send you an email alert if a login is detected from an unrecognised device, so you can take action if it wasn&apos;t you</li>
          </ul>
          <p>This processing is carried out under our legitimate interest in keeping your account secure, in accordance with GDPR Article 6(1)(f).</p>
        </div>
        <div className={styles.consentSection}>
          <h6>6. Your Rights</h6>
          <ul>
            <li>You may request deletion or modification of your profile or data at any time</li>
            <li>You may opt out of public visibility features</li>
            <li>Contact us at <a href="mailto:info@ai2peat.ie" className={styles.consentLink}>info@ai2peat.ie</a></li>
          </ul>
        </div>
        <div className={styles.consentConclusion}>
          <p>By registering, you confirm your agreement with this consent policy and acknowledge that you have read and understood these terms.</p>
          {!hasScrolledToBottom && (
            <div className={styles.scrollIndicator}>
              👇 Please scroll to the bottom to enable the &quot;I Agree&quot; button
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
}
